import type { ReactNode } from "react";
import { Button } from "./button";
import { cn } from "../lib/cn";
import { useUiLabels } from "../lib/ui-labels";

export interface PaginationProps {
  /** Zero-based, the way Spring Data pages are. */
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** Already interpolated: "Strona 2 z 7". The package has no dictionary to build it from. */
  label: ReactNode;
  /** Rendered on the left, before the page counter — a row count, a hidden-rows note. */
  children?: ReactNode;
  className?: string;
  testId?: string;
}

/**
 * Previous · "page n of m" · next (US-37.8, UX-21).
 *
 * <p>Twenty-nine screens wrote this by hand in seven shapes: some with `Button`, some with a bare
 * `text-primary hover:underline` anchor, the counter sometimes on the left and sometimes in the
 * middle, and four of them without a `disabled` on the first page, so "previous" on page one asked
 * the server for page −1. The API is the one the hand-written ones already had — `page`,
 * `totalPages`, `onChange` — so replacing them was a matter of deleting markup.
 *
 * <p>Renders nothing for a single page: a pager under a list that has no second page is furniture.
 */
export function Pagination({ page, totalPages, onChange, label, children, className, testId }: PaginationProps) {
  const labels = useUiLabels();
  if (totalPages <= 1) {
    return children ? <div className={cn("flex items-center text-sm", className)}>{children}</div> : null;
  }
  return (
    <div
      className={cn("flex flex-wrap items-center justify-between gap-3 text-sm", className)}
      data-testid={testId}
    >
      <span className="text-muted-foreground">{children}</span>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{label}</span>
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 0}
          aria-label={labels.previous}
          onClick={() => onChange(Math.max(0, page - 1))}
          data-testid={testId ? `${testId}-previous` : undefined}
        >
          {labels.previous}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page + 1 >= totalPages}
          aria-label={labels.next}
          onClick={() => onChange(page + 1)}
          data-testid={testId ? `${testId}-next` : undefined}
        >
          {labels.next}
        </Button>
      </div>
    </div>
  );
}
